import { BillingPlan, PlanAction, SubscriptionPlan } from '@shared/models/billing-plan.model';

export function normalizeCycle(cycle: string | undefined | null): 'monthly' | 'yearly' {
  if (cycle === 'monthly' || cycle === 'month') {
    return 'monthly';
  }
  return 'yearly';
}

export function getSelectedCycle(isPlanYearly: boolean): 'monthly' | 'yearly' {
  return isPlanYearly ? 'yearly' : 'monthly';
}

export function getPlanAudienceLabel(plan: BillingPlan): string {
  const fromApi = plan.description?.trim();
  if (fromApi) {
    return fromApi;
  }

  const name = plan.name.toLowerCase();
  if (name.includes('starter')) {
    return 'Perfect for individuals';
  }
  if (name.includes('growth')) {
    return 'Ideal for growing teams';
  }
  if (name.includes('pro')) {
    return 'Built for organizations';
  }
  return 'Built for modern social teams';
}

export function sortPlans(plans: BillingPlan[]): BillingPlan[] {
  return [...plans].sort((a, b) => a.sort_order - b.sort_order);
}

export function sortFeatures(features: BillingPlan['features']): BillingPlan['features'] {
  return [...features].sort((a, b) => a.display_order - b.display_order);
}

export function getAnnualMonthlyDisplayPrice(yearlyAmount: number): number {
  return Math.ceil(yearlyAmount / 12);
}

export function getPlanDisplayPrice(plan: BillingPlan, isYearly: boolean): number {
  return isYearly ? getAnnualMonthlyDisplayPrice(plan.yearly_amount) : plan.monthly_amount;
}

export const WORKSPACE_FEATURE_TOOLTIP =
  'A workspace allows you to manage one Twitter account, one Facebook page, one Instagram business account, one Threads account, one LinkedIn profile/page, one YouTube channel, one Pinterest account, and one TikTok account.';

export function isWorkspacePlanFeature(featureName: string | null | undefined): boolean {
  if (!featureName?.trim()) {
    return false;
  }

  return /\bworkspaces?\b/i.test(featureName.trim());
}

const WORKSPACE_LIMIT_KEYS = ['Workspaces', 'NumberOfWorkspaces', 'WorkspaceCount', 'workspaces'];

function parseWorkspaceCountFromText(text: string): number | null {
  if (/unlimited\s+workspaces?/i.test(text)) {
    return null;
  }

  const match = text.match(/(\d+)\s+workspaces?\b/i);
  if (match) {
    const count = Number.parseInt(match[1], 10);
    return Number.isNaN(count) || count <= 0 ? null : count;
  }

  return null;
}

export function getPlanWorkspaceCount(plan: BillingPlan | null | undefined): number {
  if (!plan) {
    return 1;
  }

  for (const key of WORKSPACE_LIMIT_KEYS) {
    const value = plan.limits?.[key];
    if (value != null && value !== '') {
      const parsed = Number(value);
      if (!Number.isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
  }

  for (const feature of plan.features ?? []) {
    const count = parseWorkspaceCountFromText(feature.name);
    if (count != null) {
      return count;
    }
  }

  return 1;
}

export function getPlanWorkspaceCheckoutLabel(plan: BillingPlan | null | undefined): string {
  if (!plan) {
    return '1 workspace';
  }

  for (const feature of plan.features ?? []) {
    if (/unlimited\s+workspaces?/i.test(feature.name)) {
      return 'Unlimited workspaces';
    }
  }

  const count = getPlanWorkspaceCount(plan);
  return count === 1 ? '1 workspace' : `${count} workspaces`;
}

export function normalizePlanKey(name: string | undefined | null): string {
  if (!name) {
    return '';
  }
  return name
    .toLowerCase()
    .replace(/\s+plan$/i, '')
    .trim();
}

export function findPlanByName(plans: BillingPlan[], name: string): BillingPlan | undefined {
  const exact = plans.find((plan) => plan.name === name);
  if (exact) {
    return exact;
  }

  const normalized = normalizePlanKey(name);
  if (!normalized) {
    return undefined;
  }

  return plans.find((plan) => normalizePlanKey(plan.name) === normalized);
}

export function getCurrentSortOrder(
  plans: BillingPlan[],
  subscriptionName: string | undefined
): number | null {
  if (!subscriptionName) {
    return null;
  }
  const currentPlan = findPlanByName(plans, subscriptionName);
  return currentPlan ? currentPlan.sort_order : null;
}

export function isLegacySubscriber(
  plans: BillingPlan[],
  subscription: SubscriptionPlan | null | undefined
): boolean {
  if (!subscription?.name) {
    return false;
  }
  return !findPlanByName(plans, subscription.name);
}

export function isCurrentPlan(
  plan: BillingPlan,
  subscription: SubscriptionPlan,
  isPlanYearly: boolean
): boolean {
  return (
    plan.name === subscription.name &&
    normalizeCycle(subscription.cycle) === getSelectedCycle(isPlanYearly)
  );
}

export function isAnnualSwitch(
  plan: BillingPlan,
  subscription: SubscriptionPlan,
  isPlanYearly: boolean
): boolean {
  return (
    plan.name === subscription.name &&
    normalizeCycle(subscription.cycle) === 'monthly' &&
    isPlanYearly
  );
}

export function isTierUpgrade(plan: BillingPlan, currentSortOrder: number | null): boolean {
  if (currentSortOrder === null) {
    return true;
  }
  return plan.sort_order > currentSortOrder;
}

export function isDowngrade(plan: BillingPlan, currentSortOrder: number | null): boolean {
  if (currentSortOrder === null) {
    return false;
  }
  return plan.sort_order < currentSortOrder;
}

export function getPlanAction(
  plan: BillingPlan,
  subscription: SubscriptionPlan | null | undefined,
  isPlanYearly: boolean,
  plans: BillingPlan[]
): PlanAction {
  if (!subscription?.name) {
    return 'purchase';
  }

  const currentPlan = findPlanByName(plans, subscription.name);
  const currentSortOrder = currentPlan?.sort_order ?? null;

  if (!currentPlan) {
    return 'upgrade';
  }

  if (isDowngrade(plan, currentSortOrder)) {
    return 'downgrade';
  }

  if (isCurrentPlan(plan, subscription, isPlanYearly)) {
    return 'current';
  }

  if (
    plan.name === subscription.name &&
    normalizeCycle(subscription.cycle) === 'yearly' &&
    !isPlanYearly
  ) {
    return 'unavailable';
  }

  if (isAnnualSwitch(plan, subscription, isPlanYearly)) {
    return 'annual_switch';
  }

  if (isTierUpgrade(plan, currentSortOrder)) {
    return 'upgrade';
  }

  return 'unavailable';
}

export function isPlanActionable(action: PlanAction): boolean {
  return action === 'purchase' || action === 'upgrade' || action === 'annual_switch';
}

export function getPlanButtonLabel(action: PlanAction, planName?: string): string {
  switch (action) {
    case 'current':
      return 'Current Plan';
    case 'annual_switch':
      return 'Switch to Annual';
    case 'upgrade':
      return planName ? `Upgrade to ${planName}` : 'Upgrade';
    case 'purchase':
      return 'Get Started';
    default:
      return '';
  }
}

export function hasTierUpgradePath(
  plans: BillingPlan[],
  subscription: SubscriptionPlan | null | undefined
): boolean {
  if (!subscription?.name) {
    return true;
  }

  if (isLegacySubscriber(plans, subscription)) {
    return true;
  }

  const currentSortOrder = getCurrentSortOrder(plans, subscription.name);
  return plans.some((plan) => isTierUpgrade(plan, currentSortOrder));
}

export function shouldPromoteAnnualSwitch(
  plans: BillingPlan[],
  subscription: SubscriptionPlan | null | undefined
): boolean {
  if (!subscription?.name || normalizeCycle(subscription.cycle) !== 'monthly') {
    return false;
  }

  if (isLegacySubscriber(plans, subscription)) {
    return false;
  }

  return !hasTierUpgradePath(plans, subscription);
}

export function hasAnyUpgradePath(
  plans: BillingPlan[],
  subscription: SubscriptionPlan | null | undefined
): boolean {
  if (!subscription?.name) {
    return true;
  }

  if (isLegacySubscriber(plans, subscription)) {
    return true;
  }

  return plans.some((plan) =>
    [true, false].some((isYearly) =>
      isPlanActionable(getPlanAction(plan, subscription, isYearly, plans))
    )
  );
}

export function shouldShowUpgradeNav(
  workspace: { planInfo?: any } | null | undefined,
  plans: BillingPlan[]
): boolean {
  if (!workspace?.planInfo) {
    return false;
  }

  const trialInfo = workspace.planInfo.trialInfo;
  if (trialInfo?.isInTrial || trialInfo?.has_expired_trial) {
    return true;
  }

  const subscription = workspace.planInfo.subscriptionInfo?.subscription;
  if (!subscription) {
    return true;
  }

  const plan = subscription.plan;
  if (subscription.status !== 'active') {
    return true;
  }

  if (!plans.length) {
    return normalizeCycle(plan?.cycle) === 'monthly';
  }

  if (isLegacySubscriber(plans, plan)) {
    return true;
  }

  return hasAnyUpgradePath(plans, plan);
}

export function getUpgradeNavTooltip(
  plans: BillingPlan[],
  subscription: SubscriptionPlan | null | undefined
): string {
  if (plans.length && subscription && shouldPromoteAnnualSwitch(plans, subscription)) {
    return 'Switch to annual billing and save 2 months';
  }
  return 'Upgrade your plan';
}
